import {
    useEffect,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ISeparatorProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    warnOnce
} from "../../utils/development";
import Text from "../text";

const Separator: FC<ISeparatorProps> = ({
    color = "subtle",
    customLocalize,
    customColor,
    customTheme,
    isShowTitle,
    orientation,
    direction,
    length,
    style,
    title,
    ...props
}) => {
    const {
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const currentOrientation = orientation ?? direction ?? "horizontal";

    const isTitleVisible = isShowTitle ?? !!title;

    const {
        container: containerDynamicStyle,
        indicator: indicatorDynamicStyle
    } = useStyles({
        orientation: currentOrientation,
        customColor,
        borders,
        colors,
        length,
        spaces,
        color
    });

    useEffect(() => {
        if(direction === undefined) {
            return;
        }

        warnOnce({
            message: "Separator: \"direction\" is deprecated and will be removed in the next major version. Use \"orientation\" instead."
        });
    }, [
        direction
    ]);

    const renderIndicator = (order: "first" | "last") => {
        if(order === "first" && !isTitleVisible) {
            return null;
        }

        return <View
            aria-orientation={currentOrientation === "vertical" ? "vertical" : undefined}
            style={[
                stylesheet.indicator,
                indicatorDynamicStyle
            ]}
            role="separator"
        />;
    };

    const renderTitle = () => {
        if(!isTitleVisible) {
            return null;
        }

        return <Text
            customTheme={customTheme}
            style={stylesheet.title}
            variant="bodySmallSize"
            color="low"
        >
            {title ? title : localize("or")}
        </Text>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderIndicator("first")}
        {renderTitle()}
        {renderIndicator("last")}
    </View>;
};

export const Seperator: FC<ISeparatorProps> = (props) => {
    useEffect(() => {
        warnOnce({
            message: "Seperator: the component name is deprecated and will be removed in the next major version. Use \"Separator\" instead."
        });
    }, []);

    return <Separator
        {...props}
    />;
};

export default Separator;
